/**
 * Keeps the homepage hero video playing on its own, on every device.
 *
 * The theme calls play() once; if the browser refuses (which phones do unless
 * the muted/playsInline *properties* are set, not just the attributes) it gives
 * up and shows the native controls instead — that's the play button you had to
 * press. This forces those properties, retries when the video becomes visible,
 * and retries again after the first user gesture, then clears the fallback
 * controls once it's actually running.
 */
(function () {
  'use strict';

  var SELECTOR = 'video[data-type="mp4"], .hero video, video.video-div';

  function videos() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function force(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.autoplay = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('loop', '');
  }

  function cleanupFallback(video) {
    video.removeAttribute('controls');
    var node = video;
    while (node && node !== document.body) {
      if (node.classList) node.classList.remove('video-interactable');
      node = node.parentNode;
    }
  }

  function attempt(video) {
    if (!video || (!video.paused && !video.ended)) return;
    force(video);

    var promise = video.play();
    if (promise && typeof promise.then === 'function') {
      promise.then(function () {
        cleanupFallback(video);
      }).catch(function () {
        /* Still blocked — a later gesture or visibility change will retry. */
      });
    } else {
      cleanupFallback(video);
    }
  }

  function attemptAll() {
    videos().forEach(attempt);
  }

  function init() {
    var list = videos();
    if (!list.length) return;

    list.forEach(function (video) {
      force(video);
      attempt(video);

      video.addEventListener('loadeddata', function () { attempt(video); });
      video.addEventListener('canplay', function () { attempt(video); });

      /* Some browsers pause offscreen video; restart when it scrolls back. */
      if (window.IntersectionObserver) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) attempt(video);
          });
        }, { threshold: 0.1 }).observe(video);
      }
    });

    /* Any first interaction unlocks playback in browsers that blocked it. */
    ['touchstart', 'click', 'scroll', 'keydown'].forEach(function (name) {
      window.addEventListener(name, attemptAll, { once: true, passive: true });
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) attemptAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
